/**
 * Fase 6 — vision-agent data layer (upload + draft review).
 *
 * Wraps the /api/ai vision endpoints:
 *   - useVisionUpload()   POST /api/ai/vision/upload  (multipart photo → base64 webp)
 *   - useDrafts(convId)   GET  /api/ai/drafts?conversation_id=…  (pending review cards)
 *   - useConfirmDraft()   POST /api/ai/drafts/:id/confirm  (edited payload → real row)
 *   - useDiscardDraft()   POST /api/ai/drafts/:id/discard
 *   - useConfirmDrafts()  POST /api/ai/drafts/confirm-batch  («Lagre N av M», #163)
 *   - useDiscardDrafts()  POST /api/ai/drafts/discard-batch  («Forkast alle», #163)
 *
 * Upload + confirm + discard are requireAdminScope-gated, so all three throw the
 * shared ApiError; callers run them through runGuarded so a 403 admin-scope
 * error opens <AdminReauthModal> and retries (mirrors ChoreTemplateAdminSection).
 *
 * Drafts are NOT WS-synced — they're private to the admin's conversation. The
 * assistant stream refetches ['ai-drafts', convId] when a *_draft tool_call_end
 * arrives, and confirm/discard invalidate it directly.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AiDraftDTO,
  ConfirmDraftBatchItem,
  DraftBatchResult,
  CreateChoreDraftInput,
  CreateCalendarEventDraftInput,
  CreateMealDraftInput,
} from '@stell/shared';
import { ApiError } from '../admin/useMembers';
import { useSession } from '../auth/useSession';

async function toApiError(res: Response): Promise<ApiError> {
  let code = 'unknown';
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body.error === 'string') code = body.error;
  } catch {
    /* non-JSON body */
  }
  return new ApiError(res.status, code);
}

export interface VisionUploadResult {
  data: string;
  media_type: 'image/webp';
  bytes: number;
}

/** Upload a photo; returns base64 webp the caller passes into the chat request. */
export function useVisionUpload() {
  return useMutation<VisionUploadResult, ApiError, File>({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await fetch('/api/ai/vision/upload', {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as VisionUploadResult;
    },
  });
}

export function draftsKey(conversationId: string | undefined) {
  return ['ai-drafts', conversationId ?? '∅'] as const;
}

export function useDrafts(conversationId: string | undefined) {
  const { data: session } = useSession();
  const isAdmin = session?.member?.role === 'admin';
  return useQuery<AiDraftDTO[]>({
    queryKey: draftsKey(conversationId),
    enabled: isAdmin && Boolean(conversationId),
    queryFn: async () => {
      const res = await fetch(`/api/ai/drafts?conversation_id=${conversationId}`, {
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { drafts: AiDraftDTO[] };
      return body.drafts;
    },
  });
}

export type DraftPayload = CreateChoreDraftInput | CreateCalendarEventDraftInput | CreateMealDraftInput;

export function useConfirmDraft(conversationId: string | undefined) {
  const qc = useQueryClient();
  // `override` is the parent's explicit opt-in past a meal draft's allergen
  // HARD-BLOCK (the server returns 422 allergen_blocked without it).
  return useMutation<AiDraftDTO, ApiError, { id: string; payload: DraftPayload; override?: boolean }>({
    mutationFn: async ({ id, payload, override }) => {
      const res = await fetch(`/api/ai/drafts/${id}/confirm`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(override ? { payload, override: true } : { payload }),
      });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { draft: AiDraftDTO };
      return body.draft;
    },
    // #163: også ved feil. En 422 allergen_blocked lagrer funnet på utkastet,
    // og kortet viser sperren først etter neste henting.
    onSettled: () => invalidateAfterConfirm(qc, conversationId),
  });
}

function invalidateAfterConfirm(qc: QueryClient, conversationId: string | undefined): void {
  void qc.invalidateQueries({ queryKey: draftsKey(conversationId) });
  // A confirmed homework lands on /lekser; an event on the calendar; a meal
  // on /meny and (via the shopping diff) the shared shopping list.
  void qc.invalidateQueries({ queryKey: ['homework'] });
  void qc.invalidateQueries({ queryKey: ['calendar'] });
  void qc.invalidateQueries({ queryKey: ['admin', 'chore-templates'] });
  void qc.invalidateQueries({ queryKey: ['meals'] });
  void qc.invalidateQueries({ queryKey: ['shopping', 'items'] });
}

async function postBatch(path: string, body: unknown): Promise<DraftBatchResult> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as DraftBatchResult;
}

/**
 * «Lagre N av M» (D13, #163). Serveren lagrer hvert utkast som er lov og
 * melder resten i `skipped` — en middag med allergentreff uten `override`
 * lagres aldri, uansett hva klienten sender. Hentes på nytt også ved feil:
 * en batch som feilet halvveis kan ha lagret noe.
 */
export function useConfirmDrafts(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<DraftBatchResult, ApiError, ConfirmDraftBatchItem[]>({
    mutationFn: (items) => postBatch('/api/ai/drafts/confirm-batch', { items }),
    onSettled: () => invalidateAfterConfirm(qc, conversationId),
  });
}

/** «Forkast alle» (D13, #163): forkaster utkastene forelderen ser. */
export function useDiscardDrafts(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<DraftBatchResult, ApiError, string[]>({
    mutationFn: (ids) => postBatch('/api/ai/drafts/discard-batch', { ids }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: draftsKey(conversationId) });
    },
  });
}

export function useDiscardDraft(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<AiDraftDTO, ApiError, string>({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/ai/drafts/${id}/discard`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { draft: AiDraftDTO };
      return body.draft;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: draftsKey(conversationId) });
    },
  });
}
